<template>
  <page-template :title="pageTitle" :description="pageDescription" :image="portalImage">
    <!-- Header Intro Block -->
    <header-intro-block
      :title="portalTitle"
      :preamble="portalPreamble"
      :image="portalImage"
      :mascot="portalMascots"
      :kicker="portalKicker"
      :includes="portalIncludes"
      :button-text="portalButtonText"
      :button-url="portalButtonUrl"
    />

    <!-- Quiz Block -->
    <div class="container">
      <quiz />
    </div>

    <!-- Upper blocks (CTAs above animal slider) -->
    <template v-if="upperBlocks.length > 0">
      <generic-block v-for="block in upperBlocks" :key="block.sys.id" :block-data="block" />
    </template>

    <!-- Joke Block -->
    <div class="container">
      <joke-block />
    </div>

    <div class="container">
      <animal-slider :animals="animals" />
    </div>

    <!-- Lower blocks (CTAs below animal slider) -->
    <template v-if="lowerBlocks.length > 0">
      <generic-block v-for="block in lowerBlocks" :key="block.sys.id" :block-data="block" />
    </template>

    <!-- Instagram Widget -->
    <!-- Temporary removed, until new Access Token is available -->
    <!-- <instagram-widget /> -->
    <section class="container py-8">
      <!-- Title - left aligned -->
      <h2 class="text-center font-headline text-h2 text-slate-darker">Följ oss i sociala medier</h2>
      <p class="mb-6 py-2 text-center">
        Vi finns på Instagram, TikTok och Facebook och använder samma användarnamn överallt:
        <strong>@RaddaDjurenklubben</strong>
      </p>
      <div class="social my-10 flex justify-around md:my-10 md:justify-center">
        <a
          v-for="(media, i) in socialmedia"
          :key="i"
          class="m-4 h-10 w-10 transition-all duration-500 ease-in-out hover:scale-105"
          :href="media.url"
        >
          <img
            class="social-icon block h-10 w-10 invert"
            :src="media.img"
            :alt="`Rädda Djuren-klubben på ${media.altText}`"
          />
        </a>
      </div>
    </section>

    <div v-if="!isMounted" hidden>
      <nuxt-link v-for="route in routes" :key="route" :to="route" />
    </div>
  </page-template>
</template>

<script setup lang="ts">
// PREPARED FOR HEADERINTROBLOCK: All portal logic is ready, just uncomment the HeaderIntroBlock in template
// and comment out the rdk-app-card when ready to switch
import type { Article, ContentfulAnimal } from '@/types/contentful'
import { transformArticleToAnimal } from '@/utils/contentfulTransformers'
import { transformContentfulPortal } from '@/utils/contentfulTransformers'

const useIsMounted = () => {
  const isMounted = ref(false)
  onMounted(() => {
    isMounted.value = true
  })
  return isMounted
}

const isMounted = useIsMounted()

// Fetch homepage portal data from Contentful
const { getPortalBySlug } = useContentful()
const homepagePortal = await useAsyncData('homepage-portal', async () => {
  try {
    const portalResponse = await getPortalBySlug('/')
    if (portalResponse) {
      return transformContentfulPortal(portalResponse)
    }
    return null
  } catch (error) {
    console.error('Error fetching homepage portal from Contentful:', error)
    return null
  }
})

const socialmedia = [
  {
    altText: 'instagram',
    url: 'https://www.instagram.com/raddadjurenklubben',
    img: '/images/instagram-logo.svg',
  },
  {
    altText: 'facebook',
    url: 'https://www.facebook.com/RaddaDjuren',
    img: '/images/facebook.svg',
  },
  {
    altText: 'tiktok',
    url: 'https://www.tiktok.com/@raddadjurenklubben',
    img: '/images/tik-tok-logo.svg',
  },
]
// Extract portal data for HeaderIntroBlock
const portalTitle = computed(() => {
  return homepagePortal.data.value?.title || ''
})

const portalPreamble = computed(() => {
  return homepagePortal.data.value?.preamble || ''
})

const portalImage = computed(() => {
  return homepagePortal.data.value?.image || null
})

const portalMascots = computed(() => {
  return homepagePortal.data.value?.mascot || []
})

const portalKicker = computed(() => {
  return homepagePortal.data.value?.kicker || undefined
})

const portalButtonText = computed(() => {
  return homepagePortal.data.value?.urlText || ''
})

const portalButtonUrl = computed(() => {
  return homepagePortal.data.value?.url || ''
})

const portalIncludes = computed(() => {
  return homepagePortal.data.value?.includes || null
})

// Get upper blocks (CTAs above animal slider)
const upperBlocks = computed(() => {
  if (homepagePortal.data.value?.upperBlocks) {
    return homepagePortal.data.value.upperBlocks
  }
  return []
})

// Get lower blocks (CTAs below animal slider)
const lowerBlocks = computed(() => {
  if (homepagePortal.data.value?.lowerBlocks) {
    return homepagePortal.data.value.lowerBlocks
  }
  return []
})

// Fetch animals from Contentful
const { getAnimals } = useContentful()
const animalsData = await useStatic('animals', async () => {
  try {
    const response = await getAnimals({
      limit: 20, // Increased to account for filtered animals
      include: 2, // Include 2 levels of references to resolve images and other linked content
    })
    // Transform Contentful articles to the format expected by components
    const transformedAnimals = response.items.map((article: Article) =>
      transformArticleToAnimal(article, response.includes),
    )

    // Filter out animals with slider set to false (Nej)
    const filteredAnimals = transformedAnimals.filter((animal: ContentfulAnimal) => animal.slider !== false)

    // Prioritize animals explicitly marked for slider, then randomize the rest
    const prioritized = filteredAnimals.filter((a: ContentfulAnimal) => a.slider === true)
    const others = filteredAnimals.filter((a: ContentfulAnimal) => a.slider !== true)
    const shuffledOthers = others.sort(() => Math.random() - 0.5)
    const ordered = [...prioritized, ...shuffledOthers]

    return ordered
  } catch (error) {
    console.error('Error fetching animals from Contentful:', error)
    return []
  }
})

const animals = computed(() => animalsData as ContentfulAnimal[])

// Generate routes for static generation
const routes = await useStatic('routes', async () => {
  try {
    const { getArticles } = useContentful()
    const response = await getArticles({ limit: 100 })
    return response.items.map((article) => `/${article.fields.slug}`)
  } catch (error) {
    console.error('Error fetching routes from Contentful:', error)
    return []
  }
})

// Get meta information from portal data or use defaults
const pageTitle = computed(() => {
  return portalTitle.value || 'Rädda Djuren-klubben'
})

const pageDescription = computed(() => {
  return (
    portalPreamble.value ||
    'Rädda Djuren är en unik klubb för alla barn som bryr sig om djur. Vårt uppdrag är att sprida fakta om djur och att lära ut empati för djur till barn och unga.'
  )
})
</script>
